// client/src/pages/SharedCollectionPage.js — /shared/<token>: properties an agent picked
// for a client. No login; the link is the key, it expires, and the agent can withdraw it.
// Opening it, hearting a property and asking a question are logged on the client's
// timeline for the agent (lib/listingShares). Never indexed (X-Robots-Tag + robots.txt).
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PropertyCard } from './PropertiesPage';
import { IdentityBlock, Disclaimer } from '../components/public/BrokerageIdentity';
import { InquiryForm } from './PropertyPage';

const SharedCollectionPage = () => {
    const { token } = useParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [favs, setFavs] = useState([]);
    const [asking, setAsking] = useState(null);

    useEffect(() => {
        let meta = document.querySelector('meta[name="robots"]');
        if (!meta) { meta = document.createElement('meta'); meta.name = 'robots'; document.head.appendChild(meta); }
        meta.content = 'noindex, nofollow';
        fetch(`/api/public/properties/shares/${encodeURIComponent(token)}`)
            .then(async (r) => { const j = await r.json(); if (!r.ok) throw new Error(j.message); return j; })
            .then((j) => { setData(j); setFavs(j.favorites || []); document.title = j.title; })
            .catch((e) => setError(e.message || 'This link has expired.'));
        return () => { meta.content = ''; };
    }, [token]);

    const toggle = async (key) => {
        const on = !favs.includes(key);
        setFavs((f) => (on ? [...f, key] : f.filter((k) => k !== key)));
        await fetch(`/api/public/properties/shares/${encodeURIComponent(token)}/favorite`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ listing_key: key, on }) }).catch(() => {});
    };

    if (error) return <div className="container mx-auto px-4 py-16 text-center"><h1 className="text-2xl font-bold text-slate-900">{error}</h1></div>;
    if (!data) return <p className="container mx-auto px-4 py-16 text-slate-700" role="status">Loading…</p>;

    return (
        <div className="container mx-auto px-4 py-8 sm:px-6">
            <h1 className="text-3xl font-bold text-slate-900">{data.title}</h1>
            {data.agent && (
                <p className="mt-1 text-slate-700">From {data.agent.name}{data.agent.license_number ? ` (${data.agent.licence_label}${data.agent.license_number})` : ''}
                    {data.agent.phone ? <> · <a className="underline" href={`tel:${data.agent.phone}`}>{data.agent.phone}</a></> : null}
                    {data.agent.email ? <> · <a className="underline" href={`mailto:${data.agent.email}`}>{data.agent.email}</a></> : null}</p>
            )}
            {data.message && <p className="mt-4 whitespace-pre-line rounded-lg bg-white p-4 text-slate-800">{data.message}</p>}
            {data.listings.length === 0 && <p className="mt-6 text-slate-700">These properties are no longer on the market. Please ask your agent for an update.</p>}
            <ul className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {data.listings.map((l) => (
                    <li key={l.key} id={l.key}>
                        <PropertyCard l={l} identity={data.identity} action={(
                            <div className="flex flex-wrap gap-2">
                                <button type="button" onClick={() => toggle(l.key)} aria-pressed={favs.includes(l.key)}
                                    className="rounded-md border border-rose-700 px-3 py-1 text-sm font-semibold text-rose-800 hover:bg-rose-50 focus:outline-none focus:ring-4 focus:ring-rose-300">
                                    {favs.includes(l.key) ? '♥ I like this' : '♡ I like this'}
                                </button>
                                <button type="button" onClick={() => setAsking(asking === l.key ? null : l.key)} aria-expanded={asking === l.key}
                                    className="rounded-md border border-slate-800 px-3 py-1 text-sm font-semibold text-indigo-800 hover:bg-indigo-50 focus:outline-none focus:ring-4 focus:ring-indigo-300">
                                    Ask / see it
                                </button>
                                {asking === l.key && (
                                    <div className="mt-2 w-full">
                                        <InquiryForm listingKey={l.key} compact endpoint={`/api/public/properties/shares/${encodeURIComponent(token)}/inquiry`} />
                                    </div>
                                )}
                            </div>
                        )} />
                    </li>
                ))}
            </ul>
            <div className="mt-8 max-w-md"><IdentityBlock identity={data.identity} /></div>
            <Disclaimer text={data.disclaimer} />
        </div>
    );
};

export default SharedCollectionPage;
